import { Card, Metadata } from "@dropbox/dash-component-library";
import { List } from "@dropbox/dig-components/list";
import { Truncate } from "@dropbox/dig-components/truncate";
import { Text } from "@dropbox/dig-components/typography";
import type { SourceRowConfig } from "@mirage/mosaics/Chat/components/compose-sources/ComposeSourceRow";
import {
  getSourceRowParts,
  removeUnderscorePrefix,
} from "@mirage/mosaics/Chat/components/compose-sources/ComposeSourceRow";
import { openURL } from "@mirage/service-platform-actions";
import type { SearchFilter } from "@mirage/shared/search/search-filters";
import classNames from "classnames";
import { memo, useEffect, useState } from "react";
import styles from "./AnswerCitationCard.module.css";

import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import { PAP_Click_SourceFile } from "@mirage/analytics/events/types/click_source_file";
import { SessionManagerName } from "@mirage/analytics/session/session-manager";
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import SearchRelevance from "@mirage/search/SearchResults/SearchRelevance";
import { RelevanceRubric } from "@mirage/service-compute-relevance/service/types";
import { useAnnotationMode } from "@mirage/service-settings/useSettings";
import useSearchQueryId from "@mirage/shared/hooks/useSearchQueryId";
import Sentry from "@mirage/shared/sentry";

const DEFAULT_CITATION_CARD_CONFIG: SourceRowConfig = {
  time: {
    timeAgo: {
      abbreviateTime: true,
      abbreviateDate: true,
    },
    hidePrefix: true,
  },
  icon: {
    hasBackground: false,
    size: "small",
  },
  subtitle: {
    hideServiceName: true,
    fallbackToServiceName: true,
  },
};

const CitationTitleCard = (titleString: string, config?: SourceRowConfig) => {
  return (
    <Text
      size="medium"
      className={
        config?.dynamicTitleHeight ? undefined : styles.AnswerCitationCardTitle
      }
    >
      <Truncate lines={2}>{removeUnderscorePrefix(titleString)}</Truncate>
    </Text>
  );
};

interface AnswerCitationCardProps {
  activeFilters?: SearchFilter[];
  query?: string;
  source: ChatSource;
  config?: SourceRowConfig;
  conversationId?: string;
  searchSessionId?: string;
  logComposeEvent?: (
    event: PAPEvent,
    overrides?: { actionSurfaceComponent?: ActionSurfaceComponent },
  ) => void;
  assistRequestId?: string;
  listItem?: boolean;
}
export const AnswerCitationCard = memo(
  ({
    activeFilters,
    query,
    source,
    config = DEFAULT_CITATION_CARD_CONFIG,
    conversationId,
    searchSessionId,
    logComposeEvent,
    assistRequestId,
    listItem,
  }: AnswerCitationCardProps) => {
    const sourceRowParts = getSourceRowParts(source, config);
    const [sourceIcon] = useState(sourceRowParts?.icon);
    const { searchQueryUuid } = useSearchQueryId();
    const { reportSessionAwarePapEvent } = useMirageAnalyticsContext();
    const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
      e.preventDefault();
      if (
        sourceRowParts?.mirageResult?.url &&
        source.type !== "local_file_content"
      ) {
        openURL(sourceRowParts.mirageResult.url);
        if (logComposeEvent) {
          logComposeEvent(
            PAP_Click_SourceFile({
              featureLine: "assist",
              actionType: "search_result",
              actionSurfaceComponent: "compose_chat_pane",
              conversationId,
              searchSessionId,
              searchQueryUuid,
              assistRequestId,
              resultUuid: sourceRowParts.mirageResult.uuid,
            }),
          );
        } else {
          reportSessionAwarePapEvent(
            [SessionManagerName.Search],
            (sessionAwareness) =>
              PAP_Click_SourceFile({
                featureLine: "assist",
                actionType: "search_result",
                actionSurfaceComponent: "compose_chat_pane",
                conversationId,
                searchQueryUuid,
                assistRequestId,
                resultUuid: sourceRowParts.mirageResult.uuid,
                ...sessionAwareness,
              }),
          );
        }
      }
    };

    useEffect(() => {
      // Send a message to Sentry if the subtitle is empty
      if (sourceRowParts && !sourceRowParts.subtitle) {
        Sentry.withScope((scope) => {
          scope.setTag("sourceRowPartsUuid", sourceRowParts.mirageResult.uuid);
          scope.setTag("sourceRowPartsTitle", sourceRowParts.titleString);
          scope.setContext(`AnswerCitationCard error context`, {
            sourceRowPartsUuid: sourceRowParts.mirageResult.uuid,
            sourceRowPartsTitle: sourceRowParts.titleString,
          });
          Sentry.captureMessage(
            `AnswerCitationCard subtitle is empty`,
            "info",
            {},
            scope,
          );
        });
      }
    }, [sourceRowParts]);

    const { isAnnotationModeOn } = useAnnotationMode();

    if (!sourceRowParts) {
      return null;
    }

    if (listItem) {
      return (
        <List.Item
          onClick={handleClick}
          spacing="small"
          paddingLeft={0}
          paddingRight={0}
          className={classNames(
            styles.AnswersCursorPointer,
            styles.AnswerCitationListItem,
          )}
          href={sourceRowParts.mirageResult.url ?? "#"}
        >
          <List.Accessory className={styles.AnswerCitationListAccessory}>
            {sourceIcon}
          </List.Accessory>
          <List.Content className={styles.AnswerCitationListContent}>
            <Text size="medium">
              <Truncate
                maxWidth={isAnnotationModeOn && query ? 170 : 200}
                tooltipControlProps={{ auto: true, placement: "top" }}
              >
                {removeUnderscorePrefix(sourceRowParts.titleString)}
              </Truncate>
            </Text>
          </List.Content>
          <List.Accessory>
            {sourceRowParts.subtitle ||
              sourceRowParts.mirageResult.fileTypeInfo?.displayName}
          </List.Accessory>
          {isAnnotationModeOn && query && (
            <List.Accessory>
              <SearchRelevance
                rubric={RelevanceRubric.Category}
                query={query}
                noIcon
                activeFilters={activeFilters}
                searchResult={sourceRowParts.mirageResult}
              />
            </List.Accessory>
          )}
        </List.Item>
      );
    }

    return (
      <Card
        variant="outline"
        isLink
        className={classNames(styles.AnswerCitationCard, {
          [styles.AnswerCitationCardNoBorder]: config?.removeBorder,
        })}
        style={{
          padding: "var(--dig-spacing__micro__medium) 4px", // Explicitly apply padding to override parent padding
        }}
      >
        <Card.Contents breakout="large" paddingBottom={"4"}>
          {source.type === "local_file_content" ? (
            <div>{CitationTitleCard(sourceRowParts.titleString, config)}</div>
          ) : (
            <Card.OverlayLink
              onClick={handleClick}
              href={sourceRowParts.mirageResult.url ?? "#"}
            >
              {CitationTitleCard(sourceRowParts.titleString, config)}
            </Card.OverlayLink>
          )}
        </Card.Contents>
        <Card.Contents breakout="large">
          <Metadata
            size="small"
            withDividers="blank"
            className={styles.AnswerCitationCardMetadata}
          >
            <Metadata.Item>{sourceRowParts.icon}</Metadata.Item>
            <Metadata.Item>
              {sourceRowParts.subtitle ||
                sourceRowParts.mirageResult.fileTypeInfo?.displayName}
            </Metadata.Item>
          </Metadata>
          {isAnnotationModeOn && query && (
            <span className={styles.AnswerCitationCardSearchRelevance}>
              <SearchRelevance
                rubric={RelevanceRubric.Category}
                query={query}
                chip={false}
                activeFilters={activeFilters}
                searchResult={sourceRowParts.mirageResult}
              />
            </span>
          )}
        </Card.Contents>
      </Card>
    );
  },
);
AnswerCitationCard.displayName = "AnswerCitationCard";
